import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, AlertTriangle, Database, Send, Mail, Users, ShieldCheck, RefreshCw } from "lucide-react";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatNextRun } from "@/lib/format";

const HEALTH_META = {
  green:  { label: "ALL SYSTEMS NOMINAL", color: "text-[#00FFA3]", border: "border-[#00FFA3]/40", bg: "bg-[#00FFA3]/10",  Icon: CheckCircle2 },
  yellow: { label: "ATTENTION NEEDED",    color: "text-[#F59E0B]", border: "border-[#F59E0B]/40", bg: "bg-[#F59E0B]/10",  Icon: AlertTriangle },
  red:    { label: "DEGRADED",            color: "text-[#FF3B30]", border: "border-[#FF3B30]/40", bg: "bg-[#FF3B30]/10",  Icon: AlertCircle  },
};

/**
 * /admin Diagnostics card — single API hit shows DB, scheduler, Resend, last
 * digest run and an overall green/yellow/red pill.
 */
export default function DiagnosticsCard({ authHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDiag = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/diagnostics`, { headers: authHeaders() });
      setData(res.data);
    } catch (err) {
      console.error("Diagnostics fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchDiag();
  }, [fetchDiag]);

  if (!data) {
    return (
      <Card className="glass-effect border-white/5" data-testid="diagnostics-loading">
        <CardContent className="p-5 text-sm text-gray-500">Loading diagnostics…</CardContent>
      </Card>
    );
  }

  const meta = HEALTH_META[data.health] || HEALTH_META.yellow;
  const { Icon } = meta;

  return (
    <Card className={`glass-effect ${meta.border}`} data-testid="diagnostics-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${meta.bg} ${meta.border} border flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${meta.color}`} />
            </div>
            <div>
              <div className={`text-sm font-bold ${meta.color}`} style={{ fontFamily: "Unbounded" }} data-testid="diag-health-label">
                {meta.label}
              </div>
              <div className="text-[10px] text-gray-500">Pipeline health rollup</div>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchDiag}
            disabled={loading}
            className="text-xs text-gray-400 hover:text-white"
            data-testid="diag-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <DiagItem
            icon={Database}
            label="Database"
            value={data.db_ok ? "OK" : "DOWN"}
            ok={data.db_ok}
            testid="diag-db"
          />
          <DiagItem
            icon={Send}
            label="Scheduler"
            value={
              data.scheduler.running
                ? data.scheduler.next_run_time ? formatNextRun(data.scheduler.next_run_time) : "Running"
                : "Stopped"
            }
            ok={data.scheduler.running}
            testid="diag-scheduler"
          />
          <DiagItem
            icon={Mail}
            label="Resend"
            value={
              !data.resend.configured ? "No key" :
              data.resend.domain_verified ? "Verified" : "Sandbox"
            }
            ok={data.resend.configured && data.resend.domain_verified}
            warn={data.resend.configured && !data.resend.domain_verified}
            testid="diag-resend"
          />
          <DiagItem
            icon={Users}
            label="Subscribed"
            value={data.subscribed_users}
            ok
            testid="diag-users"
          />
        </div>

        {data.last_digest_run && (
          <div className="mt-4 pt-4 border-t border-white/5" data-testid="diag-last-run">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              <ShieldCheck className="w-3 h-3" /> Last digest fan-out
            </div>
            <div className="grid grid-cols-4 gap-3 text-center">
              <SmallStat label="Sent" value={data.last_digest_run.sent} color="text-[#00FFA3]" />
              <SmallStat label="Empty" value={data.last_digest_run.skipped_empty} />
              <SmallStat label="No key" value={data.last_digest_run.skipped_no_key} color={data.last_digest_run.skipped_no_key ? "text-[#F59E0B]" : ""} />
              <SmallStat label="Errors" value={data.last_digest_run.errors} color={data.last_digest_run.errors ? "text-[#FF3B30]" : ""} />
            </div>
            <div className="text-[10px] text-gray-600 mt-2">
              {new Date(data.last_digest_run.ran_at).toLocaleString()}
            </div>
          </div>
        )}

        {!data.resend.domain_verified && data.resend.configured && (
          <div className="mt-4 text-[11px] text-gray-400 leading-relaxed" data-testid="diag-sandbox-hint">
            <span className="text-[#F59E0B]">→</span> Verify a domain at <span className="mono text-gray-200">resend.com/domains</span> and update{" "}
            <span className="mono text-gray-200">SENDER_EMAIL</span> in <span className="mono text-gray-200">/app/backend/.env</span> to deliver to any recipient.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DiagItem({ icon: Icon, label, value, ok, warn, testid }) {
  const color = ok ? "text-[#00FFA3]" : warn ? "text-[#F59E0B]" : "text-[#FF3B30]";
  return (
    <div className="glass-effect rounded-lg p-3 border border-white/5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-2">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-base font-bold mono ${color}`} data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

function SmallStat({ label, value, color = "text-white" }) {
  return (
    <div>
      <div className={`text-lg font-bold mono ${color}`}>{value ?? 0}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}
